import { Capability } from '__types__';
import { maybe, rd } from '@passionware/monads';
import {
  ArrowLeft,
  Blocks,
  Building2,
  Plus,
  Settings,
  Shield,
  Type,
  Users
} from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import { withinSettings } from 'v4/core/appRoutes';
import type { AppRouting } from 'v5/core/routing/appRouting';
import { resolveWorkspaceSlugForLinks } from 'v5/core/app-scope/resolveWorkspaceSlugForLinks';
import { appRouting } from 'v5/core/routing/appRouting';
import { EnterpriseWorkspaceSettingsCollapsible } from 'v5/features/core/enterprise/EnterpriseWorkspaceSettingsCollapsible';
import { useTranslation } from 'react-i18next';
import { useManageEnterpriseFeature } from 'v5/features/core/enterprise/useManageEnterpriseFeature';
import { Button } from 'v5/design-system/Button';
import { EnterpriseMenuCollapsibleSkeleton } from 'v5/features/core/enterprise/EnterpriseMenuCollapsibleSkeleton';
import { MenuItem, MenuSeparator } from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarCollapse,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarSectionHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import { cn } from 'v5/platform/dom/cn';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithRoutingService } from 'v5/services/front/RoutingService/RoutingService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import { WithThemeService } from 'v5/services/front/ThemeService/ThemeService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import {
  isSettingsPath,
  SETTINGS_RETURN_TO_KEY
} from 'v5/core/routing/routeChangeEvent';

export interface EnterpriseSettingsSidebarProps
  extends WithServices<
    [
      WithRoutingService,
      WithFeatureService,
      WithSupportService,
      WithThemeService,
      WithOnlineStatusService,
      WithEnterpriseService,
      WithUserFlowService,
      WithAppScopeService,
      WithAccessService
    ]
  > {
  className?: string;
}

export function EnterpriseSettingsSidebar({
  services,
  className
}: EnterpriseSettingsSidebarProps) {
  const history = useHistory();
  const { pathname } = useLocation();
  const scopeRd = services.appScopeService.useAppScope();
  const scopeRdStable = rd.useLastWithPlaceholder(scopeRd);
  const scope = rd.tryGet(scopeRdStable);
  const organizationId = scope?.organizationId ?? '';
  const legacyTeamSettings = withinSettings(organizationId);
  const orgRouting: AppRouting = appRouting.org(organizationId);
  const settings = orgRouting.settings;

  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId ? maybe.of(organizationId) : maybe.ofAbsent()
  );
  const workspaces = rd.tryGet(workspacesRd);
  const activeWorkspaceSlug = scope
    ? resolveWorkspaceSlugForLinks(scope, pathname, workspaces)
    : '';

  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const { t: tResourceTypes } = useTranslation('core', {
    keyPrefix: 'resourceTypesAdmin'
  });
  const can = services.accessService.useCan();
  const canConfigureResourceTypes = rd.getOrElse(
    can({ action: Capability.CONFIGURE_RESOURCE_TYPES }),
    false
  );
  const canManage = useManageEnterpriseFeature(services.featureService);
  const backToApp = orgRouting.workspace(activeWorkspaceSlug).resources.root();

  const handleBack = () => {
    const storedReturnTo = sessionStorage.getItem(SETTINGS_RETURN_TO_KEY);
    if (storedReturnTo && !isSettingsPath(storedReturnTo)) {
      history.push(storedReturnTo);
    } else {
      history.push(backToApp);
    }
  };

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex items-center gap-2 min-h-(--header-height) px-2">
        <Button variant="ghost" size="sm" layout="icon" onClick={handleBack}>
          <ArrowLeft className="size-4" />
        </Button>
        <span className="flex-1 text-fg-default">Settings</span>
        <DockableSidebarToggle>
          <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
        </DockableSidebarToggle>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        <SidebarGroup label="Your account">
          <NavLink to={settings.personal.account()}>
            <MenuItem icon={<Users className="size-4" />}>
              Personal account
            </MenuItem>
          </NavLink>
          <NavLink to={settings.personal.security()}>
            <MenuItem icon={<Shield className="size-4" />}>Security</MenuItem>
          </NavLink>
        </SidebarGroup>

        <SidebarGroup label="Global">
          <NavLink to={settings.global.users()}>
            <MenuItem icon={<Users className="size-4" />}>Users</MenuItem>
          </NavLink>
          <NavLink to={settings.global.permissionGroups()}>
            <MenuItem icon={<Users className="size-4" />}>
              Permission groups
            </MenuItem>
          </NavLink>
          <NavLink to={settings.global.authentication()}>
            <MenuItem icon={<Shield className="size-4" />}>
              Authentication
            </MenuItem>
          </NavLink>
          <NavLink to={legacyTeamSettings.settings.team.fields()}>
            <MenuItem icon={<Settings className="size-4" />}>
              Custom fields
            </MenuItem>
          </NavLink>
          {canConfigureResourceTypes ? (
            <NavLink to={settings.global.resourceTypes()}>
              <MenuItem icon={<Type className="size-4" />}>
                {tResourceTypes('globalTitle')}
              </MenuItem>
            </NavLink>
          ) : null}
          <NavLink to={settings.global.integrations()}>
            <MenuItem icon={<Blocks className="size-4" />}>
              Integrations
            </MenuItem>
          </NavLink>
          {canManage ? (
            <NavLink to={settings.global.manageEnterprise()}>
              <MenuItem icon={<Building2 className="size-4" />}>
                {t('manageEnterprise')}
              </MenuItem>
            </NavLink>
          ) : null}
        </SidebarGroup>

        <MenuSeparator />
        <SidebarSectionHeader>Workspace settings</SidebarSectionHeader>
        {rd
          .journey(workspacesRd)
          .wait(<EnterpriseMenuCollapsibleSkeleton count={2} />)
          .catch(() => (
            <div className="px-3 py-2 text-[13px] text-fg-muted">
              {t('workspacesUnavailable')}
            </div>
          ))
          .map(workspacesList => (
            <>
              {workspacesList.map(workspace => (
                <EnterpriseWorkspaceSettingsCollapsible
                  key={workspace.id}
                  services={services}
                  workspace={workspace}
                  organizationId={organizationId}
                  scope={scope}
                  canManage={canManage}
                  canConfigureResourceTypes={canConfigureResourceTypes}
                />
              ))}
              {canManage ? (
                <MenuItem
                  className="text-fg-muted cursor-pointer"
                  icon={<Plus className="size-4" />}
                  onClick={event => {
                    event.preventDefault();
                    services.userFlowService.openCreateEnterpriseWorkspaceDialog(
                      organizationId
                    );
                  }}
                >
                  {t('newWorkspace')}
                </MenuItem>
              ) : null}
            </>
          ))}
      </SidebarContent>

      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}
